import { Injectable } from '@angular/core';
import _ from 'lodash';
import jQuery from 'jquery';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ChildProject, IChildProject, State } from '../models/projects/project.model';
import { Request } from '../models/easydiadem/request.model';
import { LivecoAPIService } from './liveco-api.service';
import { FlowType } from '../models/proofreading.model';
import { Id } from '../models/base.model';
import { Query, ResolverParams, ResolverService } from './api/resolver.service';
import { KFeathers } from './api/feathers.service';
import { environment } from 'src/environments/environment';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { LoaderService } from './loader.service';
import { AuthService } from './auth.service';
import { TranslateService } from '@ngx-translate/core';
import { ArchivesDialogComponent } from '../shared/components/dialog/archives-dialog/archives-dialog.component';
import { ConfirmDialogComponent } from '../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { RequestsService } from './requests.service';
import { MasterProject } from '../models/projects/master-project.model';
import { ProductionTextsService } from './production-texts.service';
import { AlertService } from './alert.service';


// WRITE HERE THE EMAIL OF USERS WHO CAN ARCHIVE PROJECTS
// const archive_users = [
//     "cpp-diadem@superdev.fr",
//     "j.grandjean@diadem-pack.com",
//     "t.grandjean@diadem-pack.com",
//     "b.kichenin@diadem-pack.com",
//     "p.nuzillat@diadem-pack.com",
//     "f.gautret@diadem-pack.com",
//     "a.soury@diadem-pack.com"
// ];


@Injectable({
    providedIn: 'root',
})
export class ProjectService extends ResolverService<ChildProject> {
    path: string = 'project';
    model: new (any: any) => ChildProject = ChildProject;

    onNewStatusChange: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    // TO DO Fix childJob and childData inconsistency (same for masterJob)
    protected _childJob: BehaviorSubject<ChildProject | null> = new BehaviorSubject<any>(null);
    protected _masterJob: BehaviorSubject<MasterProject | null> = new BehaviorSubject<any>(null);


    get childSubject() {
        return this._childJob;
    }
    get masterSubject() {
        return this._masterJob;
    }

    get masterData(): MasterProject | null { return this._masterJob.value; };
    set masterData(m: any) {
        this._masterJob.next(m);
        this.onNewStatusChange.next(_.isNil(this._masterJob.value?._id));
    }

    get childData() { return this._childJob?.value; };
    set childData(m: any) {
        this._childJob.next(m);
        this.onNewStatusChange.next(_.isNil(this._childJob.value?._id));
    }

    get masterId() { return this.masterData?._id || ""; }
    set masterId(s: string) {
        if (this._masterJob.value)
            this._masterJob.value._id = s;

        this._masterJob.next(this._masterJob.value);
        this.onNewStatusChange.next(_.isNil(this.masterData?._id));
    }

    get childId() { return this.childData?._id; }
    set childId(s: string) {
        if (this._childJob.value)
            this._childJob.value._id = s;

        this._childJob.next(this._childJob.value);
        this.onNewStatusChange.next(_.isNil(this.childData?._id));
    }


    // EZD
    easydiadem_project_creation: boolean = false;
    requestId: string | undefined = undefined;
    get ed_request$(): BehaviorSubject<Request | undefined> {
        return this.s_requests.current$;
    }
    get ed_request(): Request | undefined {
        return this.s_requests.current;
    }
    set ed_request(r: Request | undefined) {
        this.s_requests.current = r;
    }

    isNewProject(type: string = 'child'): boolean {
        return (type === 'child') ? (_.isNil(this.childId) || _.isEmpty(this.childId)) : (_.isNil(this.masterId) || _.isEmpty(this.masterId));
    }

    reset() {
        this.masterData = {};
        this.childData = {};
        this.resetCurrent();
        this.resetEntities();
    }

    refresh() {
        this.emitCurrent();
        // this.masterSubject.next(this.masterData);
        // this.childSubject.next(this.childData);
    }

    makeSourcePantone(options: any[]) {
        let source = [];
        source = options.flatMap((currentValue, index) => {
            return currentValue.colors.flatMap((item: string) => {
                return { slug: currentValue.title, value: item };
            });
        });
        return source;
    }



    constructor(
        protected feathers: KFeathers,
        public s_requests: RequestsService,
        public s_productionTexts: ProductionTextsService,
        protected dialog: MatDialog,
        protected loader: LoaderService,
        protected auth: AuthService,
        protected translate: TranslateService,
        protected alert: AlertService,
        protected liveco: LivecoAPIService) {
        super(feathers);
    }

    async getAvailableFlowType(projectID: Id) {
        const project: IChildProject = await this.get(projectID) as any;
        if (!project)
            return [];

        const flow = [];
        if (project?.custom?.general?.flows) {
            flow.push(..._.uniq(project.custom.general.flows));
        }
        // if (project.custom.general.service_exe) {
        //     flow.push(FlowType.EXE);
        // }
        // if (project.custom.general.service_repro) {
        //     flow.push(FlowType.REPRO);
        // }
        if (project.custom.easydiadem?.length) {
            return project.custom.easydiadem?.map(request => request.flow).filter(flow => flow);
        }
        return flow;
    }

    openFileInPackZ(url: string) {
        jQuery.ajax({
            type: "POST",
            url: "http://127.0.0.1:9091/frameCGI/frame.cgi",
            contentType: "text/plain; charset=UTF-8",
            data: JSON.stringify({ application_name: "PACKZ", method: "frame.os.open_file_in_application", file_url: url })
        });
    }

    openFolderChild(project: IChildProject) {
        if (project && project.folders && project.folders.length) {
            let folderPath = project.folders[0] && project.folders[0].url ? project.folders[0].url : "";

            if (project?.folders[0]?.url) {
                if (project?.state === State.ARCHIVED) {
                    const baseUrl = project.folders[0].url;

                    const os = this.getOperatingSystem();

                    if (os === 'Windows') {
                        folderPath = baseUrl.replace(environment.cloudflow_url_base, environment.archives.windows);
                    }

                    if (os === "Mac") {
                        folderPath = baseUrl.replace(environment.cloudflow_url_base, environment.archives.mac);
                    }

                } else {
                    folderPath = project?.folders[0].url;
                }
            } else {
                folderPath = "";
            }

            this.openFolder(folderPath);
        }
    }

    openFolderParent(project: IChildProject) {
        if (project && project.folders && project.folders.length) {
            let folderPath = project.folders[0] && project.folders[0].url ? project.folders[0].url : "";

            if (project?.folders[0]?.url) {
                if (project?.state === State.ARCHIVED) {
                    const baseUrl = project.folders[0].url;

                    const os = this.getOperatingSystem();

                    if (os === 'Windows') {
                        folderPath = baseUrl.replace(environment.cloudflow_url_base, environment.archives.windows);
                    }

                    if (os === "Mac") {
                        folderPath = baseUrl.replace(environment.cloudflow_url_base, environment.archives.mac);
                    }
                } else {
                    folderPath = project?.folders[0].url;
                }
            } else {
                folderPath = "";
            }

            if (folderPath && folderPath.split('/').length > 1) {
                folderPath = folderPath.split('/').slice(0, -1).join('/');
            }

            this.openFolder(folderPath);
        }
    }

    openFolder(url: string) {
        jQuery.ajax({
            type: "POST",
            url: "http://127.0.0.1:9091/frameCGI/frame.cgi",
            contentType: "text/plain; charset=UTF-8",
            data: JSON.stringify({ method: "frame.os.open_folder_in_os", folder_url: url })
        });
    }

    getOperatingSystem() {
        let operatingSystem = 'Inconnu';
        if (navigator.userAgent.indexOf('Win') !== -1) { operatingSystem = 'Windows'; }
        if (navigator.userAgent.indexOf('Mac') !== -1) { operatingSystem = 'Mac'; }
        if (navigator.userAgent.indexOf('X11') !== -1) { operatingSystem = 'UNIX'; }
        if (navigator.userAgent.indexOf('Linux') !== -1) { operatingSystem = 'Linux'; }
        return operatingSystem;
    }

    openFolderOld(childJob: any) {
        if (childJob && childJob.folders && childJob.folders.length) {
            let folderPath = childJob.custom.general.absolute_url;

            //folderPath = "/Volumes/Archives 2/Pack/ST ROLAN/P2302183 - T_JOB 25359_Logo St Rolan/2. Exe/Relecture001/_DOCS_DEFINITIFS/"

            const os = this.getOperatingSystem();

            if (folderPath && (os === 'Windows' || os === 'Mac')) {

                let finalPath = "";

                const splittedPath = folderPath.split('/');

                for (let i = 0; i < splittedPath.length - 1; i++) {
                    finalPath += splittedPath[i] + "/";
                }

                if (os === "Mac") {
                    finalPath = finalPath.replace(environment.regex_xraid_server, environment.volumes_mac).replace(environment.regex_srv_data_01, environment.volumes_mac);
                }

                // if (os === "Windows") {
                //     finalPath = finalPath.replace(/\//g, '\\');
                // }

                if (finalPath.indexOf('XRAID-SERVEUR/8 Archives') !== -1) {
                    finalPath = finalPath.replace('XRAID-SERVEUR/8 Archives', 'SRV-DATA-01/Archives 1');
                }

                if (finalPath.indexOf('XRAID-SERVEUR\\8 Archives') !== -1) {
                    finalPath = finalPath.replace('XRAID-SERVEUR\\8 Archives', 'SRV-DATA-01\\Archives 1');
                }

                this.openFolder(finalPath);
            }
        }
    }

    // ARCHIVE && DISARCHIVAGE
    /**
     * Ouvre la confirmation d'archivage / desarchivage.
     * Les deux actions possibles sont explicites : lancer maintenant, ou programmer
     * l'operation pour le prochain traitement automatique.
     * @param archived - true pour un archivage, false pour un desarchivage
     */
    private async confirmArchiveAction(archived: boolean): Promise<"now" | "later" | false> {
        const result = await firstValueFrom(
            this.dialog
                .open(ArchivesDialogComponent, {
                    panelClass: "dialog",
                    data: {
                        title: "ARE_YOU_SURE",
                        text: archived ? "ARCHIVES.CONFIRM_ARCHIVE" : "ARCHIVES.CONFIRM_UNARCHIVE",
                        hint: "ARCHIVES.SCHEDULE_HINT",
                        valid: archived ? "ARCHIVES.ARCHIVE_NOW" : "ARCHIVES.UNARCHIVE_NOW",
                        noConfirm: false,
                        later: archived ? "ARCHIVES.SCHEDULE_ARCHIVE" : "ARCHIVES.SCHEDULE_UNARCHIVE",
                    },
                    width: "450px"
                })
                .afterClosed()
        );

        if (result === "now") return "now";
        if (result === "later") return "later";
        return false;
    }

    /**
     * Message d'erreur a afficher : le serveur renvoie deja un libelle explicite
     * (ex. "Archivage annule : le dossier d'archives ... est inaccessible"), on le
     * prefere au message generique.
     */
    private archiveErrorMessage(err: any, fallback: string): string {
        const message = err?.message?.replace('[updateProject] ', '')?.trim();
        return message || fallback;
    }

    private async scheduleArchiveLog(identifier: string, archived: boolean): Promise<boolean> {
        try {
            await this.liveco.create("/archive-logs/scheduled", { identifier, archived });
            this.alert.success(archived ? "ARCHIVES.SCHEDULED_ARCHIVE_OK" : "ARCHIVES.SCHEDULED_UNARCHIVE_OK", { duration: 8000 });
            return true;
        } catch (err: any) {
            this.alert.error(this.archiveErrorMessage(err, archived ? "ARCHIVES.SCHEDULED_ARCHIVE_KO" : "ARCHIVES.SCHEDULED_UNARCHIVE_KO"), { duration: 10000 });
            return false;
        }
    }

    /**
     * Demande d'archivage / desarchivage encore en file d'attente pour ce projet, si elle existe.
     * Sert a proposer l'annulation depuis la fiche projet, sans passer par la page d'archives.
     */
    async getScheduledLog(identifier: string | undefined): Promise<any | null> {
        if (!this.canArchive() || !identifier) return null;

        try {
            const res: any = await this.liveco.find({ identifier }, "/archive-logs/scheduled");
            return res?.result || null;
        } catch (err: any) {
            console.error("[PROJECT] getScheduledLog", err);
            return null;
        }
    }

    /**
     * Annule une demande d'archivage / desarchivage encore en file d'attente.
     * Le serveur rend au projet l'etat qu'il avait avant sa mise en file.
     * @param logId - identifiant de la ligne d'archivage a annuler
     * @returns true si l'annulation a abouti
     */
    async cancelScheduled(logId: string | undefined, archived: boolean): Promise<boolean> {
        if (!this.canArchive() || !logId) {
            return false;
        }

        const confirmed = await firstValueFrom(
            this.dialog
                .open(ConfirmDialogComponent, {
                    panelClass: "dialog",
                    data: {
                        title: "ARE_YOU_SURE",
                        text: archived ? "ARCHIVES.CONFIRM_CANCEL_ARCHIVE" : "ARCHIVES.CONFIRM_CANCEL_UNARCHIVE",
                        valid: "ARCHIVES.CANCEL_SCHEDULE",
                        noConfirm: false
                    },
                    width: "450px"
                })
                .afterClosed()
        );

        if (!confirmed) return false;

        this.loader.loading = true;
        try {
            await this.liveco.delete(logId, "/archive-logs/scheduled");
            this.alert.success(archived ? "ARCHIVES.CANCEL_ARCHIVE_OK" : "ARCHIVES.CANCEL_UNARCHIVE_OK", { duration: 8000 });
            return true;
        } catch (err: any) {
            this.alert.error(this.archiveErrorMessage(err, "ARCHIVES.CANCEL_KO"), { duration: 15000 });
            return false;
        } finally {
            this.loader.loading = false;
        }
    }

    async archive(identifier: string | undefined, many: boolean = false) {
        if (!this.canArchive() || !identifier) {
            return undefined;
        }

        if (!many) {
            const response = await this.confirmArchiveAction(true);
            if (!response) return undefined;

            if (response === "later") {
                // 'scheduled' : rien n'est archive tout de suite, mais l'appelant doit
                // rafraichir son affichage (le projet passe en archiving-in-progress).
                return (await this.scheduleArchiveLog(identifier, true)) ? 'scheduled' : undefined;
            }
        }

        if (!this.auth?.user?.email) return undefined;

        this.loader.loading = true;
        try {
            const res = await this.liveco.find({ identifier }, "/project-archive");
            if (res?.result) {
                this.childData = res.result;
            }
            if (!many) {
                this.alert.success("ARCHIVES.ARCHIVE_OK");
            }
            return res?.result;
        } catch (err: any) {
            // archiveMany construit sa propre liste d'echecs : on ne l'inonde pas d'alertes.
            if (many) throw err;
            this.alert.error(this.archiveErrorMessage(err, "ARCHIVES.ARCHIVE_KO"), { duration: 15000 });
            return undefined;
        } finally {
            this.loader.loading = false;
        }
    }

async archiveMany(identifiers: Array<string | undefined>) {
    const validIdentifiers = identifiers.filter((id): id is string => !!id);

    if (!this.canArchive() || !validIdentifiers.length) {
        return { succeeded: [], failed: [] };
    }

    const response = await this.confirmArchiveAction(true);
    if (!response || !this.auth?.user?.email) return { succeeded: [], failed: [] };

    if (response === "later") {
        await Promise.all(validIdentifiers.map(id => this.scheduleArchiveLog(id, true)));
        return { succeeded: [], failed: [] };
    }

    const succeeded: string[] = [];
    const failed: { identifier: string; reason: string }[] = [];

    this.loader.loading = true;
    try {
        for (const identifier of validIdentifiers) {
            try {
                const result = await this.archive(identifier, true);
                if (result) {
                    succeeded.push(identifier);
                } else {
                    failed.push({ identifier, reason: 'ARCHIVES.FAILED_UNKNOWN' });
                }
            } catch (err: any) {
                const reason =  err?.message?.replace('[updateProject] ', '') || 'ARCHIVES.FAILED_UNKNOWN';
                failed.push({ identifier, reason });
            }
        }
    } finally {
        this.loader.loading = false;
    }

    return { succeeded, failed };
}
    async unarchive(identifier: string | undefined) {
        if (!this.canArchive() || !identifier) return undefined;

        const response = await this.confirmArchiveAction(false);
        if (!response) return undefined;

        if (response === "later") {
            return (await this.scheduleArchiveLog(identifier, false)) ? 'scheduled' : undefined;
        }

        if (!this.auth?.user?.email) return undefined;

        this.loader.loading = true;
        try {
            const res = await this.liveco.find({ identifier }, "/project-unarchive");
            if (res?.result) this.childData = res.result;
            this.alert.success("ARCHIVES.UNARCHIVE_OK");
            return res?.result;
        } catch (err: any) {
            this.alert.error(this.archiveErrorMessage(err, "ARCHIVES.UNARCHIVE_KO"), { duration: 15000 });
            return undefined;
        } finally {
            this.loader.loading = false;
        }
    }

    canArchive(): boolean {
        // return !!(this.auth && this.auth.user && this.auth.user.email && archive_users.includes(this.auth.user.email));
        return this.auth?.canArchive();
    }

    isArchived(): boolean {
        return this.childData?.state === State.ARCHIVED;
    }

    override find(queries?: Query, params?: ResolverParams): Promise<ChildProject[]> {
        return super.find(queries, params);
    }

    async exportCSV(query: any) {
        const csvEntries = await this.liveco.find(query, '/project/export-csv');

        for (let i = 1; i < csvEntries.result.length; i++) {
            if (csvEntries.result[i][9] !== '') {
                csvEntries.result[i][9] = this.translate.instant("EXTERN.DASHBOARD." + csvEntries.result[i][9].toUpperCase());
            }
            if (csvEntries.result[i][11] !== '') {
                csvEntries.result[i][11] = this.translate.instant("INTERN.DASHBOARD." + csvEntries.result[i][11].toUpperCase());
            }
        }

        const csv = csvEntries.result.map((e: any) => e.join(';')).join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'projects_export.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

async getInactiveProject(cpp: string | undefined, brand: string | undefined, page: number, targetLimit: number, inactiveDuration: number) {
        if (this.auth && this.auth.user && this.auth.user.email && this.canArchive()) {
            if (!targetLimit) {
                console.error(`[ERROR][PROJECT_INACTIVE] targetLimit invalid`, { targetLimit });
                return undefined;
            }

        const res = await this.liveco.find(
            { cpp, brand, page, targetLimit, limitInactivity: inactiveDuration },
            "/project/inactive"
        );

            if (res?.result) {
                this.childData = res.result;
            }
            return res?.result;
        }
    }

    async getArchiveLogs(searchstring: string = '', archived: Boolean = true, unarchived: Boolean = true, limit: number = 10, page: number = 1) {
        if (this.auth && this.auth.user && this.auth.user.email && this.canArchive()) {
            const res = await this.liveco.find(
                { searchstring, archived, unarchived, limit, page },
                "/archive-logs-query"
            );

            return res?.result;
        }
    }
}
